// Keeps the portal's session (JWT) alive while a tab is open.
//
// The API issues access tokens that live 120 minutes and never renews them by itself. The portal
// kept the expired token in localStorage, so every [Authorize] route failed silently after two
// hours. On the call page only the claim depends on the JWT — SignalR is anonymous, the device twin
// and the LiveKit token go through the Functions with function keys — so an agent whose session had
// lapsed still rang, answered and joined, but WITHOUT a claim, and the telephone fallback dialled
// 20 s later (HT031-218).
//
// Two mechanisms, both here:
//   - a background check (once at load, then every minute) that renews the token before it expires;
//   - ensureFreshToken(), which the claim calls right before it needs a valid token.
//
// Decisions are pure functions; storage and HTTP are injected so everything is unit-testable.

import axios from 'axios'
import jwtDefaultConfig from '@core/auth/jwt/jwtDefaultConfig'

/** Renew when less than this remains: well inside the 120 min lifetime, well above the 1 min tick. */
export const REFRESH_THRESHOLD_MS = 10 * 60 * 1000
/** Check cadence. Chrome throttles hidden-tab timers to about once a minute, which this tolerates. */
export const CHECK_INTERVAL_MS = 60 * 1000
/** A hung API must not hold the answer button: the claim awaits this before it runs. */
const REFRESH_TIMEOUT_MS = 5000

// Same keys jwtService uses, read directly so this module stays free of the Vue/axios plugin chain.
const defaultStorage = {
  getToken: () => localStorage.getItem(jwtDefaultConfig.storageTokenKeyName),
  getRefreshToken: () => localStorage.getItem(jwtDefaultConfig.storageRefreshTokenKeyName),
  setToken: value => localStorage.setItem(jwtDefaultConfig.storageTokenKeyName, value),
  setRefreshToken: value => localStorage.setItem(jwtDefaultConfig.storageRefreshTokenKeyName, value),
}

/**
 * Read the `exp` claim (seconds since epoch) of a JWT without verifying it. The server verifies;
 * the client only needs to know WHEN to ask for a new one.
 *
 * @param {string|null|undefined} token
 * @returns {number|null} null when the token is missing or malformed
 */
export function decodeJwtExp(token) {
  if (typeof token !== 'string') return null
  const parts = token.split('.')
  if (parts.length !== 3) return null
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
    const payload = JSON.parse(atob(padded))
    return Number.isFinite(payload.exp) ? payload.exp : null
  } catch (error) {
    return null
  }
}

/**
 * Decide what the stored token needs.
 *
 * @param {number|null} expSeconds from decodeJwtExp
 * @param {number} nowMs Date.now()
 * @param {number} thresholdMs renew when less than this remains
 * @returns {'valid'|'refresh'|'expired'}
 *   'valid'   → still comfortably usable
 *   'refresh' → usable, but about to expire: renew now while the old token still authenticates
 *   'expired' → past exp, or unreadable: renewing needs the anonymous refresh route
 */
export function sessionState(expSeconds, nowMs, thresholdMs = REFRESH_THRESHOLD_MS) {
  if (!Number.isFinite(expSeconds)) return 'expired'
  const remainingMs = expSeconds * 1000 - nowMs
  if (remainingMs <= 0) return 'expired'
  if (remainingMs < thresholdMs) return 'refresh'
  return 'valid'
}

/**
 * Build the renewal function: POST /auth/refresh with the current pair, store the new pair.
 * At most one request in flight — concurrent callers share the same promise.
 *
 * Failure is deliberately passive and returns false: a 400 usually means another tab or device
 * rotated the refresh token first (the API keeps ONE per user). Nothing here logs the agent out;
 * that decision belongs to the code path that actually needs a valid token.
 */
export function createRefresher({
  storage = defaultStorage,
  http = axios,
  endpoint = jwtDefaultConfig.refreshEndpoint,
} = {}) {
  let pending = null

  const refreshOnce = async () => {
    const accessToken = storage.getToken()
    const refreshToken = storage.getRefreshToken()
    if (!accessToken || !refreshToken) return false
    try {
      const { data } = await http.post(endpoint, { accessToken, refreshToken }, {
        timeout: REFRESH_TIMEOUT_MS,
        // A PROACTIVE renewal happens while the current token still authenticates, and the API
        // required it on this route until Refresh became anonymous; harmless once it is. Same two
        // headers every other call in the app sends.
        headers: { Authorization: `Bearer ${accessToken}`, token: accessToken },
      })
      if (!data || !data.accessToken || !data.refreshToken) return false
      storage.setToken(data.accessToken)
      storage.setRefreshToken(data.refreshToken)
      return true
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Session refresh failed; keeping the current token.', error?.response?.status || error?.message)
      return false
    }
  }

  return function refreshAccessToken() {
    if (!pending) {
      pending = refreshOnce().finally(() => { pending = null })
    }
    return pending
  }
}

export const refreshAccessToken = createRefresher()

/**
 * Make sure the stored token is usable, renewing it when it is about to expire or already has.
 *
 * @param {object} [options]
 * @param {boolean} [options.force] renew even if the token looks valid (the API just refused it)
 * @returns {Promise<'valid'|'refreshed'|'failed'>}
 */
export async function ensureFreshToken({
  storage = defaultStorage,
  refresh = refreshAccessToken,
  now = Date.now,
  force = false,
} = {}) {
  const token = storage.getToken()
  if (!token) return 'failed'
  if (sessionState(decodeJwtExp(token), now()) === 'valid' && !force) return 'valid'

  if (await refresh()) return 'refreshed'

  // Another tab may have renewed it meanwhile: judge whatever is in storage NOW, not the token
  // this call started with.
  return sessionState(decodeJwtExp(storage.getToken()), now()) === 'expired' ? 'failed' : 'valid'
}

/**
 * Start the background check: once immediately, then every intervalMs. A logged-out tab (no token)
 * does nothing on each tick, so this can be started once at app boot and left running.
 *
 * @returns {() => void} stop function
 */
export function startSessionRefresh({
  storage = defaultStorage,
  ensure = ensureFreshToken,
  intervalMs = CHECK_INTERVAL_MS,
  schedule = (fn, ms) => window.setInterval(fn, ms),
  cancel = id => window.clearInterval(id),
} = {}) {
  const tick = () => {
    if (!storage.getToken()) return
    ensure().catch(() => {})
  }
  tick()
  const id = schedule(tick, intervalMs)
  return () => cancel(id)
}

/**
 * End the session the same way the @axios 401 interceptor does: clear the stored credentials and
 * go back to the login page. Used when a renewal has definitively failed and the agent must sign
 * in again — the router only checks that a token EXISTS, so leaving a dead one in place would keep
 * the agent on a page where every protected call fails.
 */
export function endSession({
  storage = localStorage,
  navigate = href => { window.location.href = href },
} = {}) {
  storage.removeItem(jwtDefaultConfig.storageTokenKeyName)
  storage.removeItem(jwtDefaultConfig.storageRefreshTokenKeyName)
  storage.removeItem('userData')
  navigate('/login')
}
